import { useState, useEffect } from 'react'
import { Star, Phone, TrendingUp, UserCheck, Users, Award, Loader2, Clock, CheckCircle, XCircle, Download, MapPin, FileText, Camera, X, Plus, Building2, Settings2, Power } from 'lucide-react'
import toast from 'react-hot-toast'
import {
  fetchCollectors, approveCollector, rejectCollector, updateCollectorZone,
  createCollector, updateCollector, fetchCompanies,
} from '../services/adminService'
import type { Company } from '../types'
import { exportToCsv } from '../utils/exportCsv'
import AuthedImage from '../components/AuthedImage'
import ZonePicker from '../components/ZonePicker'

interface CollectorApiResponse {
  id: string
  name: string
  phone: string | null
  email: string
  address: string | null
  lat: number | null
  lng: number | null
  zone: string | null
  dateOfBirth: string | null
  driverLicenseNumber: string | null
  driverLicensePhoto: string | null
  facePhoto: string | null
  approvalStatus: string | null
  isActive: boolean
  isAvailable: boolean
  maxDailyPickups: number
  companyId: string | null
  company: { id: string; name: string; status: string } | null
  createdAt: string
  metrics: {
    totalPickups: number
    completedPickups: number
    missedPickups: number
    activePickups: number
    pendingOffers: number
    averageRating: number
    totalRatings: number
    completionRate: number
  }
  /** How reliably they answer offers, over the last month. */
  responsiveness?: {
    offered: number
    accepted: number
    declined: number
    ignored: number
    acceptRate: number
    ignoreRate: number
    judgeable: boolean
  }
}

interface CollectorData {
  id: string
  name: string
  phone: string
  email: string
  address: string
  lat: number | null
  lng: number | null
  zone: string | null
  dateOfBirth: string | null
  driverLicenseNumber: string | null
  driverLicensePhoto: string | null
  facePhoto: string | null
  rating: number
  completedToday: number
  missedToday: number
  pendingToday: number
  totalPickups: number
  completionRate: number
  approvalStatus: string
  isActive: boolean
  isOnDuty: boolean
  maxDailyPickups: number
  companyId: string | null
  companyName: string | null
  activePickups: number
  pendingOffers: number
  /** Offers they never answered in the last month, and out of how many. */
  ignoredOffers: number
  offersSettled: number
  ignoreRate: number
  /** Whether they have had enough offers for the rate to mean anything. */
  responsivenessJudgeable: boolean
  joinedAt: string
}

function mapApiToCollector(c: CollectorApiResponse): CollectorData {
  return {
    id: c.id,
    name: c.name,
    phone: c.phone ?? '',
    email: c.email,
    address: c.address ?? '',
    lat: c.lat ?? null,
    lng: c.lng ?? null,
    zone: c.zone ?? null,
    dateOfBirth: c.dateOfBirth ?? null,
    driverLicenseNumber: c.driverLicenseNumber ?? null,
    driverLicensePhoto: c.driverLicensePhoto ?? null,
    facePhoto: c.facePhoto ?? null,
    rating: c.metrics.averageRating,
    completedToday: c.metrics.completedPickups,
    missedToday: c.metrics.missedPickups,
    pendingToday: c.metrics.activePickups,
    totalPickups: c.metrics.totalPickups,
    completionRate: c.metrics.completionRate,
    approvalStatus: c.approvalStatus ?? 'APPROVED',
    isActive: c.isActive,
    isOnDuty: c.isAvailable,
    maxDailyPickups: c.maxDailyPickups,
    companyId: c.companyId,
    companyName: c.company?.name ?? null,
    activePickups: c.metrics.activePickups,
    pendingOffers: c.metrics.pendingOffers,
    ignoredOffers: c.responsiveness?.ignored ?? 0,
    offersSettled: c.responsiveness?.offered ?? 0,
    ignoreRate: c.responsiveness?.ignoreRate ?? 0,
    responsivenessJudgeable: c.responsiveness?.judgeable ?? false,
    joinedAt: c.createdAt,
  }
}

const SORT_OPTIONS = [
  { value: 'rating', label: 'By Rating' },
  { value: 'pickups', label: 'By Pickups' },
  { value: 'completion', label: 'By Completion' },
  { value: 'unresponsive', label: 'Least Responsive' },
]

const STATUS_TABS = [
  { value: '', label: 'All' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
]

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star
          key={s}
          size={12}
          className={s <= Math.round(rating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-200 fill-gray-200'}
        />
      ))}
      <span className="ml-1 text-xs font-semibold text-[#1A1A2E]">{rating.toFixed(1)}</span>
    </div>
  )
}

function getInitials(name: string) {
  return name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()
}

function ApprovalBadge({ status }: { status: string }) {
  const config: Record<string, { bg: string; text: string; label: string }> = {
    PENDING: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Pending Approval' },
    APPROVED: { bg: 'bg-green-100', text: 'text-green-700', label: 'Approved' },
    REJECTED: { bg: 'bg-red-100', text: 'text-red-700', label: 'Rejected' },
  }
  const c = config[status] ?? config.APPROVED
  return (
    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${c.bg} ${c.text}`}>
      {c.label}
    </span>
  )
}

const AVATAR_COLORS = ['bg-primary-600', 'bg-blue-500', 'bg-purple-500', 'bg-orange-500', 'bg-teal-500', 'bg-pink-500']

const EMPTY_CREATE_FORM = {
  name: '',
  email: '',
  phone: '',
  password: '',
  address: '',
  zone: '',
  dateOfBirth: '',
  driverLicenseNumber: '',
  companyId: '',
  maxDailyPickups: 15,
}

/** A labelled text input, matching the styling used across the admin panel. */
function ModalField({
  label, value, onChange, placeholder, type = 'text', required = false, hint,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  placeholder?: string
  type?: string
  required?: boolean
  hint?: string
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-[#6B7280] mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-[#1A1A2E] placeholder-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
      />
      {hint && <p className="text-[10px] text-[#9CA3AF] mt-1">{hint}</p>}
    </div>
  )
}

export default function Collectors() {
  const [collectors, setCollectors] = useState<CollectorData[]>([])
  const [loading, setLoading] = useState(true)
  const [sortBy, setSortBy] = useState<'rating' | 'pickups' | 'completion' | 'unresponsive'>('rating')
  const [statusFilter, setStatusFilter] = useState('')
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [rejectModal, setRejectModal] = useState<{ id: string; name: string } | null>(null)
  const [rejectReason, setRejectReason] = useState('')
  const [imagePreview, setImagePreview] = useState<{ url: string; title: string } | null>(null)
  const [zoneModal, setZoneModal] = useState<{ id: string; name: string; currentZone: string } | null>(null)
  const [newZone, setNewZone] = useState('')
  const [companies, setCompanies] = useState<Company[]>([])
  const [createOpen, setCreateOpen] = useState(false)
  const [createForm, setCreateForm] = useState(EMPTY_CREATE_FORM)
  const [creating, setCreating] = useState(false)
  const [manageModal, setManageModal] = useState<CollectorData | null>(null)
  const [manageForm, setManageForm] = useState({ companyId: '', maxDailyPickups: 15, isAvailable: true })

  async function loadCollectors(status?: string) {
    setLoading(true)
    try {
      const data = await fetchCollectors({ status: status || undefined, limit: 100 })
      const raw: CollectorApiResponse[] = data?.collectors ?? data ?? []
      setCollectors(raw.map(mapApiToCollector))
    } catch {
      toast.error('Failed to load collectors')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadCollectors(statusFilter)
  }, [statusFilter])

  // Companies populate the assignment dropdowns; a failure here must not block
  // the page, it just means collectors can only be created as independents.
  useEffect(() => {
    fetchCompanies({ status: 'ACTIVE', limit: 100 })
      .then((data) => setCompanies(data?.companies ?? []))
      .catch(() => setCompanies([]))
  }, [])

  const handleCreate = async () => {
    const { name, email, password } = createForm
    if (!name.trim() || !email.trim() || password.length < 8) {
      toast.error('Name, email, and a password of at least 8 characters are required')
      return
    }
    // The server also needs a capital letter and a digit; say so here.
    if (!(/[A-Z]/.test(password) && /[0-9]/.test(password))) {
      toast.error('The password needs at least one capital letter and one number')
      return
    }
    setCreating(true)
    try {
      await createCollector({
        name: name.trim(),
        email: email.trim(),
        phone: createForm.phone.trim() || undefined,
        password,
        address: createForm.address.trim() || undefined,
        zone: createForm.zone.trim() || undefined,
        dateOfBirth: createForm.dateOfBirth || undefined,
        driverLicenseNumber: createForm.driverLicenseNumber.trim() || undefined,
        companyId: createForm.companyId || undefined,
        maxDailyPickups: createForm.maxDailyPickups,
      })
      toast.success(`${name.trim()} added and approved`)
      setCreateOpen(false)
      setCreateForm(EMPTY_CREATE_FORM)
      loadCollectors(statusFilter)
    } catch (err) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
      toast.error(message || 'Failed to create collector')
    } finally {
      setCreating(false)
    }
  }

  const openManage = (collector: CollectorData) => {
    setManageModal(collector)
    setManageForm({
      companyId: collector.companyId ?? '',
      maxDailyPickups: collector.maxDailyPickups,
      isAvailable: collector.isOnDuty,
    })
  }

  const handleManageSave = async () => {
    if (!manageModal) return
    setActionLoading(manageModal.id)
    try {
      await updateCollector(manageModal.id, {
        companyId: manageForm.companyId || null,
        maxDailyPickups: manageForm.maxDailyPickups,
        isAvailable: manageForm.isAvailable,
      })
      toast.success(`${manageModal.name} updated`)
      setManageModal(null)
      loadCollectors(statusFilter)
    } catch (err) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
      toast.error(message || 'Failed to update collector')
    } finally {
      setActionLoading(null)
    }
  }

  const handleApprove = async (id: string, name: string) => {
    setActionLoading(id)
    try {
      await approveCollector(id)
      toast.success(`${name} has been approved as a collector`)
      loadCollectors(statusFilter)
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Failed to approve collector')
    } finally {
      setActionLoading(null)
    }
  }

  const handleReject = async () => {
    if (!rejectModal) return
    setActionLoading(rejectModal.id)
    try {
      await rejectCollector(rejectModal.id, rejectReason || undefined)
      toast.success(`${rejectModal.name}'s application has been rejected`)
      setRejectModal(null)
      setRejectReason('')
      loadCollectors(statusFilter)
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Failed to reject collector')
    } finally {
      setActionLoading(null)
    }
  }

  const handleZoneUpdate = async () => {
    if (!zoneModal || !newZone.trim()) return
    setActionLoading(zoneModal.id)
    try {
      await updateCollectorZone(zoneModal.id, newZone.trim())
      toast.success(`Zone updated for ${zoneModal.name}`)
      setZoneModal(null)
      setNewZone('')
      loadCollectors(statusFilter)
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Failed to update zone')
    } finally {
      setActionLoading(null)
    }
  }

  const sorted = [...collectors].sort((a, b) => {
    if (sortBy === 'rating') return b.rating - a.rating
    if (sortBy === 'pickups') return b.totalPickups - a.totalPickups
    if (sortBy === 'unresponsive') {
      // Only collectors with enough offers to judge are ranked; the rest fall
      // to the bottom rather than topping the list on a single missed offer.
      const rank = (c: CollectorData) => (c.responsivenessJudgeable ? c.ignoreRate : -1)
      return rank(b) - rank(a)
    }
    return b.completionRate - a.completionRate
  })

  const pendingCount = collectors.filter((c) => c.approvalStatus === 'PENDING').length

  const stats = {
    total: collectors.length,
    activeToday: collectors.filter((c) => c.isOnDuty && c.approvalStatus === 'APPROVED').length,
    avgRating: collectors.length > 0
      ? (collectors.reduce((s, c) => s + c.rating, 0) / collectors.length).toFixed(1)
      : '—',
    pending: pendingCount,
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {[
          { label: 'Total Collectors', value: stats.total, icon: Users, color: 'text-blue-600 bg-blue-50' },
          { label: 'Active Today', value: stats.activeToday, icon: UserCheck, color: 'text-green-700 bg-green-50' },
          { label: 'Avg. Rating', value: `${stats.avgRating} / 5`, icon: Award, color: 'text-yellow-600 bg-yellow-50' },
          { label: 'Pending Approvals', value: stats.pending, icon: Clock, color: 'text-orange-600 bg-orange-50' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl ${color.split(' ')[1]} flex items-center justify-center`}>
              <Icon className={`w-5 h-5 ${color.split(' ')[0]}`} />
            </div>
            <div>
              <p className="text-xs text-[#6B7280] font-medium">{label}</p>
              <p className="text-xl font-bold text-[#1A1A2E]">{value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Status Filter Tabs + Sort */}
      <div className="flex items-center justify-between">
        <div className="flex bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
          {STATUS_TABS.map(({ value, label }) => (
            <button
              key={value}
              onClick={() => setStatusFilter(value)}
              className={`px-4 py-2 text-xs font-semibold transition-colors relative ${
                statusFilter === value ? 'bg-primary-600 text-white' : 'text-[#6B7280] hover:bg-gray-50'
              }`}
            >
              {label}
              {value === 'PENDING' && pendingCount > 0 && statusFilter !== 'PENDING' && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-orange-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                  {pendingCount}
                </span>
              )}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-[#6B7280]" />
          <div className="flex bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
            {SORT_OPTIONS.map(({ value, label }) => (
              <button
                key={value}
                onClick={() => setSortBy(value as typeof sortBy)}
                className={`px-3 py-2 text-xs font-semibold transition-colors ${
                  sortBy === value ? 'bg-primary-600 text-white' : 'text-[#6B7280] hover:bg-gray-50'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <p className="text-sm text-[#6B7280]">{collectors.length} collector{collectors.length !== 1 ? 's' : ''} found</p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              if (!collectors.length) return
              exportToCsv(
                ['Name', 'Email', 'Phone', 'Company', 'Status', 'On Duty', 'Capacity', 'Rating', 'Total Pickups', 'Completed', 'Missed', 'Completion Rate', 'Joined'],
                collectors.map((c: CollectorData) => [
                  c.name, c.email, c.phone ?? '', c.companyName ?? 'Independent', c.approvalStatus,
                  c.isOnDuty ? 'Yes' : 'No', String(c.maxDailyPickups),
                  String(c.rating), String(c.totalPickups), String(c.completedToday),
                  String(c.missedToday), `${c.completionRate}%`, c.joinedAt,
                ]),
                'collectors'
              )
            }}
            className="flex items-center gap-1.5 text-xs font-semibold text-[#1A1A2E] bg-gray-100 px-3 py-1.5 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <Download size={13} />
            Export CSV
          </button>
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="flex items-center gap-1.5 bg-primary-600 text-white text-xs font-semibold px-4 py-1.5 rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus size={14} />
            Add Collector
          </button>
        </div>
      </div>

      {/* Collector Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {sorted.map((collector, idx) => (
          <div key={collector.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition-shadow">
            {/* Header */}
            <div className="flex items-start gap-3 mb-4">
              <div className={`w-14 h-14 rounded-full ${AVATAR_COLORS[idx % AVATAR_COLORS.length]} flex items-center justify-center text-white text-lg font-bold flex-shrink-0`}>
                {getInitials(collector.name)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-bold text-[#1A1A2E] truncate">{collector.name}</p>
                  <ApprovalBadge status={collector.approvalStatus} />
                </div>
                <p className="text-xs text-[#6B7280] mt-0.5">{collector.phone || collector.email}</p>
              </div>
            </div>

            {/* Company + duty */}
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${collector.companyName ? 'bg-purple-50 text-purple-700' : 'bg-gray-100 text-gray-600'}`}>
                <Building2 size={10} />
                {collector.companyName ?? 'Independent'}
              </span>
              <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${collector.isOnDuty ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                <Power size={10} />
                {collector.isOnDuty ? 'On duty' : 'Off duty'}
              </span>
              <span className="text-[10px] text-[#6B7280]">
                {collector.activePickups}/{collector.maxDailyPickups} active
              </span>
              {collector.pendingOffers > 0 && (
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-orange-50 text-orange-700">
                  {collector.pendingOffers} offer{collector.pendingOffers !== 1 ? 's' : ''} pending
                </span>
              )}
            </div>

            {/* Location Info */}
            <div className="flex flex-wrap items-center gap-2 mb-2">
              {collector.zone && (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">
                  <MapPin size={10} />
                  {collector.zone}
                </span>
              )}
              {collector.address && (
                <span className="text-[10px] text-[#6B7280] truncate max-w-[180px]" title={collector.address}>
                  {collector.address}
                </span>
              )}
              {collector.lat != null && collector.lng != null ? (
                <span className="text-[10px] font-mono text-[#9CA3AF]">
                  {collector.lat.toFixed(4)}, {collector.lng.toFixed(4)}
                </span>
              ) : (
                <span className="text-[10px] text-orange-500 italic">No GPS</span>
              )}
            </div>

            {/* Document Review (Collectors with documents) */}
            {(collector.driverLicensePhoto || collector.facePhoto || collector.driverLicenseNumber) && (
              <div className="mb-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
                <div className="flex items-center gap-1.5 mb-2">
                  <FileText size={12} className="text-[#6B7280]" />
                  <span className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wide">Verification Documents</span>
                </div>
                {collector.driverLicenseNumber && (
                  <p className="text-xs text-[#1A1A2E] mb-2">
                    <span className="font-semibold">License:</span> {collector.driverLicenseNumber}
                  </p>
                )}
                {collector.dateOfBirth && (
                  <p className="text-xs text-[#6B7280] mb-2">
                    <span className="font-semibold">DOB:</span> {new Date(collector.dateOfBirth).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </p>
                )}
                <div className="flex gap-2">
                  {collector.driverLicensePhoto && (
                    <button
                      type="button"
                      onClick={() => setImagePreview({ url: collector.driverLicensePhoto!, title: `${collector.name} — Driver License` })}
                      className="flex-1 group relative"
                    >
                      <AuthedImage
                        path={collector.driverLicensePhoto}
                        alt="Driver License"
                        className="w-full h-20 object-cover rounded-lg border border-gray-200 group-hover:border-primary-400 transition-colors"
                      />
                      <span className="absolute bottom-1 left-1 text-[8px] font-bold bg-black/60 text-white px-1.5 py-0.5 rounded">License</span>
                    </button>
                  )}
                  {collector.facePhoto && (
                    <button
                      type="button"
                      onClick={() => setImagePreview({ url: collector.facePhoto!, title: `${collector.name} — Face Photo` })}
                      className="flex-1 group relative"
                    >
                      <AuthedImage
                        path={collector.facePhoto}
                        alt="Face Photo"
                        className="w-full h-20 object-cover rounded-lg border border-gray-200 group-hover:border-primary-400 transition-colors"
                      />
                      <span className="absolute bottom-1 left-1 text-[8px] font-bold bg-black/60 text-white px-1.5 py-0.5 rounded">Selfie</span>
                    </button>
                  )}
                </div>
                {!collector.driverLicensePhoto && !collector.facePhoto && (
                  <p className="text-[10px] text-orange-500 italic flex items-center gap-1">
                    <Camera size={10} />
                    No documents uploaded
                  </p>
                )}
              </div>
            )}

            <StarRating rating={collector.rating} />

            {/* Today's stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-3">
              {[
                { label: 'Done', value: collector.completedToday, color: 'text-green-700 bg-green-50' },
                { label: 'Pending', value: collector.pendingToday, color: 'text-blue-600 bg-blue-50' },
                { label: 'Missed', value: collector.missedToday, color: 'text-red-600 bg-red-50' },
              ].map(({ label, value, color }) => (
                <div key={label} className={`rounded-xl p-2 text-center ${color.split(' ')[1]}`}>
                  <p className={`text-lg font-bold ${color.split(' ')[0]}`}>{value}</p>
                  <p className={`text-[10px] font-medium ${color.split(' ')[0]} opacity-80`}>{label}</p>
                </div>
              ))}
            </div>

            {/* Completion bar */}
            <div className="mt-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-[#6B7280]">Completion Rate</span>
                <span className="text-xs font-bold text-[#1A1A2E]">{collector.completionRate}%</span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full">
                <div
                  className="h-2 rounded-full bg-primary-600 transition-all"
                  style={{ width: `${collector.completionRate}%` }}
                />
              </div>
              <p className="text-[10px] text-[#6B7280] mt-1">
                {collector.totalPickups.toLocaleString()} total pickups · since {new Date(collector.joinedAt).toLocaleDateString()}
              </p>
            </div>

            {/* Offer responsiveness, last 30 days.
                Separate from completion above, and the more actionable of the
                two: completion measures work they took on, this measures work
                they never replied to — the offers that sat for thirteen hours
                and then landed on an admin. */}
            {collector.offersSettled > 0 && (
              <div className="mt-2 flex items-center justify-between gap-2">
                <span className="text-[10px] text-[#6B7280]">Ignored offers (30d)</span>
                <span
                  className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                    !collector.responsivenessJudgeable
                      ? 'text-[#9CA3AF] bg-gray-50'
                      : collector.ignoreRate >= 25
                        ? 'text-red-700 bg-red-50'
                        : 'text-[#6B7280] bg-gray-50'
                  }`}
                  title={
                    collector.responsivenessJudgeable
                      ? `${collector.ignoredOffers} of ${collector.offersSettled} offers were never answered`
                      : 'Too few offers so far to judge'
                  }
                >
                  {collector.ignoredOffers}/{collector.offersSettled}
                  {collector.responsivenessJudgeable ? ` · ${collector.ignoreRate}%` : ''}
                </span>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2 mt-4">
              {collector.approvalStatus === 'PENDING' ? (
                <>
                  <button
                    onClick={() => handleApprove(collector.id, collector.name)}
                    disabled={actionLoading === collector.id}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-green-600 rounded-xl text-xs font-semibold text-white hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    <CheckCircle size={13} />
                    Approve
                  </button>
                  <button
                    onClick={() => setRejectModal({ id: collector.id, name: collector.name })}
                    disabled={actionLoading === collector.id}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-red-50 border border-red-200 rounded-xl text-xs font-semibold text-red-600 hover:bg-red-100 transition-colors disabled:opacity-50"
                  >
                    <XCircle size={13} />
                    Reject
                  </button>
                </>
              ) : (
                <>
                  <a
                    href={`tel:${collector.phone}`}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-[#F8FAF9] border border-gray-200 rounded-xl text-xs font-semibold text-[#6B7280] hover:bg-gray-100 transition-colors"
                  >
                    <Phone size={13} />
                    Contact
                  </a>
                  <button
                    type="button"
                    onClick={() => { setZoneModal({ id: collector.id, name: collector.name, currentZone: collector.zone ?? '' }); setNewZone(collector.zone ?? ''); }}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-blue-50 border border-blue-200 rounded-xl text-xs font-semibold text-blue-600 hover:bg-blue-100 transition-colors"
                  >
                    <MapPin size={13} />
                    Zone
                  </button>
                  <button
                    type="button"
                    onClick={() => openManage(collector)}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-purple-50 border border-purple-200 rounded-xl text-xs font-semibold text-purple-700 hover:bg-purple-100 transition-colors"
                  >
                    <Settings2 size={13} />
                    Manage
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {collectors.length === 0 && (
        <div className="bg-white rounded-2xl p-12 shadow-sm border border-gray-100 text-center">
          <Users className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm font-semibold text-[#6B7280]">
            {statusFilter ? `No ${statusFilter.toLowerCase()} collectors found` : 'No collectors found'}
          </p>
        </div>
      )}

      {/* Reject Modal */}
      {rejectModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl">
            <h3 className="text-lg font-bold text-[#1A1A2E] mb-1">Reject Application</h3>
            <p className="text-sm text-[#6B7280] mb-4">
              Reject <strong>{rejectModal.name}</strong>'s collector application? This action will notify them.
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Reason for rejection (optional)"
              rows={3}
              className="w-full border border-gray-200 rounded-xl p-3 text-sm text-[#1A1A2E] placeholder-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none mb-4"
            />
            <div className="flex gap-3">
              <button
                onClick={() => { setRejectModal(null); setRejectReason('') }}
                className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm font-semibold text-[#6B7280] hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={actionLoading === rejectModal.id}
                className="flex-1 py-2.5 bg-red-600 rounded-xl text-sm font-semibold text-white hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {actionLoading === rejectModal.id ? 'Rejecting...' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {imagePreview && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setImagePreview(null)}>
          <div className="relative max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white text-sm font-bold">{imagePreview.title}</h3>
              <button
                type="button"
                onClick={() => setImagePreview(null)}
                title="Close preview"
                aria-label="Close preview"
                className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
              >
                <X size={16} className="text-white" />
              </button>
            </div>
            <AuthedImage
              path={imagePreview.url}
              alt={imagePreview.title}
              className="w-full max-h-[70vh] object-contain rounded-xl border-2 border-white/20"
            />
          </div>
        </div>
      )}

      {/* Add Collector Modal */}
      {createOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white">
              <div>
                <h3 className="text-lg font-bold text-[#1A1A2E]">Add Collector</h3>
                <p className="text-xs text-[#6B7280]">
                  The account is created approved and verified — an admin creating it is the approval.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setCreateOpen(false)}
                className="p-1 rounded-lg hover:bg-gray-100 text-[#6B7280]"
                title="Close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <ModalField label="Full name" value={createForm.name} onChange={(v) => setCreateForm(f => ({ ...f, name: v }))} required placeholder="Kwame Boateng" />
                <ModalField label="Phone" value={createForm.phone} onChange={(v) => setCreateForm(f => ({ ...f, phone: v }))} placeholder="+233..." />
              </div>
              <ModalField label="Email" type="email" value={createForm.email} onChange={(v) => setCreateForm(f => ({ ...f, email: v }))} required placeholder="collector@example.com" />
              <ModalField
                label="Temporary password"
                type="password"
                value={createForm.password}
                onChange={(v) => setCreateForm(f => ({ ...f, password: v }))}
                required
                hint="At least 8 characters with one uppercase letter and one number. Share it with the collector to change on first sign-in."
              />
              <ModalField
                label="Address"
                value={createForm.address}
                onChange={(v) => setCreateForm(f => ({ ...f, address: v }))}
                placeholder="Osu, Accra"
                hint="Geocoded on save. Without coordinates the collector is never matched by proximity."
              />
              <div className="grid grid-cols-2 gap-3">
                <ModalField label="Date of birth" type="date" value={createForm.dateOfBirth} onChange={(v) => setCreateForm(f => ({ ...f, dateOfBirth: v }))} />
                <div />
              </div>
              <ZonePicker
                value={createForm.zone ? [createForm.zone] : []}
                onChange={(zones) => setCreateForm(f => ({ ...f, zone: zones[0] ?? '' }))}
                multiple={false}
                label="Operational zone"
                hint="Leave empty to derive it from the address above."
              />
              <ModalField label="Driver license number" value={createForm.driverLicenseNumber} onChange={(v) => setCreateForm(f => ({ ...f, driverLicenseNumber: v }))} placeholder="GHA-DL-000000" />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-[#6B7280] mb-1" id="create-collector-company">Company</label>
                  <select
                    value={createForm.companyId}
                    onChange={(e) => setCreateForm(f => ({ ...f, companyId: e.target.value }))}
                    aria-labelledby="create-collector-company"
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-[#1A1A2E] focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">Independent</option>
                    {companies.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#6B7280] mb-1">Daily pickup capacity</label>
                  <input
                    type="number"
                    min={1}
                    max={100}
                    value={createForm.maxDailyPickups}
                    onChange={(e) => setCreateForm(f => ({ ...f, maxDailyPickups: Number(e.target.value) || 1 }))}
                    aria-label="Daily pickup capacity"
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-[#1A1A2E] focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3 p-5 border-t border-gray-100 sticky bottom-0 bg-white">
              <button
                type="button"
                onClick={() => setCreateOpen(false)}
                className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm font-semibold text-[#6B7280] hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreate}
                disabled={creating}
                className="flex-1 py-2.5 bg-primary-600 rounded-xl text-sm font-semibold text-white hover:bg-primary-700 transition-colors disabled:opacity-50"
              >
                {creating ? 'Creating...' : 'Create collector'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manage Collector Modal */}
      {manageModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl">
            <h3 className="text-lg font-bold text-[#1A1A2E] mb-1">Manage {manageModal.name}</h3>
            <p className="text-sm text-[#6B7280] mb-4">
              Company membership, daily capacity, and duty status. All three feed the assignment engine.
            </p>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1" id="manage-collector-company">Company</label>
                <select
                  value={manageForm.companyId}
                  onChange={(e) => setManageForm(f => ({ ...f, companyId: e.target.value }))}
                  aria-labelledby="manage-collector-company"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-[#1A1A2E] focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Independent</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1">Daily pickup capacity</label>
                <input
                  type="number"
                  min={1}
                  max={100}
                  value={manageForm.maxDailyPickups}
                  onChange={(e) => setManageForm(f => ({ ...f, maxDailyPickups: Number(e.target.value) || 1 }))}
                  aria-label="Daily pickup capacity"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-[#1A1A2E] focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <p className="text-[10px] text-[#9CA3AF] mt-1">
                  Offers stop once this many pickups are booked for a day, counting offers still awaiting a reply.
                </p>
              </div>

              <label className="flex items-center gap-3 border border-gray-200 rounded-xl px-3 py-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={manageForm.isAvailable}
                  onChange={(e) => setManageForm(f => ({ ...f, isAvailable: e.target.checked }))}
                  className="w-4 h-4 accent-primary-600"
                />
                <span className="text-sm text-[#1A1A2E]">
                  On duty
                  <span className="block text-[10px] text-[#9CA3AF]">
                    Off duty stops new offers. Pickups already accepted are unaffected.
                  </span>
                </span>
              </label>
            </div>

            <div className="flex gap-3 mt-5">
              <button
                type="button"
                onClick={() => setManageModal(null)}
                className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm font-semibold text-[#6B7280] hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleManageSave}
                disabled={actionLoading === manageModal.id}
                className="flex-1 py-2.5 bg-primary-600 rounded-xl text-sm font-semibold text-white hover:bg-primary-700 transition-colors disabled:opacity-50"
              >
                {actionLoading === manageModal.id ? 'Saving...' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Zone Edit Modal */}
      {zoneModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl">
            <h3 className="text-lg font-bold text-[#1A1A2E] mb-1">Assign Zone</h3>
            <p className="text-sm text-[#6B7280] mb-4">
              Update the operational zone for <strong>{zoneModal.name}</strong>.
            </p>
            {zoneModal.currentZone && (
              <p className="text-xs text-[#6B7280] mb-2">
                Current zone: <span className="font-semibold text-blue-600">{zoneModal.currentZone}</span>
              </p>
            )}
            <div className="mb-4">
              <ZonePicker
                value={newZone ? [newZone] : []}
                onChange={(zones) => setNewZone(zones[0] ?? '')}
                multiple={false}
                label="Operational zone"
                hint="Pickups in this zone are preferred for this collector when the engine ranks candidates."
              />
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => { setZoneModal(null); setNewZone('') }}
                className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm font-semibold text-[#6B7280] hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleZoneUpdate}
                disabled={!newZone.trim() || actionLoading === zoneModal.id}
                className="flex-1 py-2.5 bg-blue-600 rounded-xl text-sm font-semibold text-white hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {actionLoading === zoneModal.id ? 'Saving...' : 'Save Zone'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
